import { API_ERROR_KIND, classifyApiError } from '../../utils/errors';
import { DELIVERY_STATUS_LABELS, toTimelineEvents } from '../orderDetails/orderUtils';

/**
 * Ninja Hubs' own support line, used when the store behind the order has not
 * published one of its own.
 */
export const SUPPORT_PHONE = '56940062756';

/**
 * What to tell somebody who is waiting for a parcel when the query failed.
 *
 * Deliberately not `extractApiError`: that one prefers the sentence the backend
 * wrote, and these endpoints write for developers — `"No orders found matching
 * the provided data"`, `"phone missing"`. English, in front of a customer.
 *
 * The 403 is the one worth knowing about. The two search endpoints rate-limit
 * to three requests a second and block rather than answer, so somebody who
 * presses Buscar twice gets a 403 — which the old page reported as "no
 * encontramos esa orden", sending them to check a number that was fine.
 *
 * Reading that 403 as the limiter is an inference, not something the body says:
 * django-ratelimit blocks by raising, and DRF renders it as the same
 * `{"detail": "You do not have permission to perform this action."}` as any
 * other denial. It holds because these three views declare
 * `permission_classes([])`, so no permission check exists that could produce a
 * 403, and `publicApi` sends no session, so there is no CSRF check either. Give
 * any of those views a permission class and this message starts lying. The
 * honest fix is on the backend: its `request.limited` branch already builds a
 * 429 and is unreachable only because the decorator passes `block=True`.
 *
 * @param {unknown} error
 * @returns {string}
 */
export const trackingErrorMessage = (error) => {
  const { kind } = classifyApiError(error);

  switch (kind) {
    case API_ERROR_KIND.NOT_FOUND:
      return 'No encontramos ningún pedido con esos datos. Revisa que estén tal como los ingresaste al comprar.';
    case API_ERROR_KIND.FORBIDDEN:
      return 'Hiciste varias consultas seguidas. Espera unos segundos y vuelve a intentar.';
    case API_ERROR_KIND.UNREACHABLE:
      return 'No pudimos conectarnos. Revisa tu conexión y vuelve a intentar.';
    case API_ERROR_KIND.TIMEOUT:
      return 'La consulta está tardando demasiado. Vuelve a intentar en un momento.';
    default:
      return 'No pudimos consultar tu pedido. Vuelve a intentar en unos minutos.';
  }
};

/**
 * Where the parcel is right now: the shipment's own status, and nothing else.
 *
 * Tracking follows the shipment, never the order. `mixed_status` answers with
 * the *order's* status while it is being prepared, so a parcel the courier
 * already had came back as "En preparación"; and the combined history ends on an
 * order event often enough that the badge read an order status too.
 *
 * Not the last history event either: the backend writes one only when a
 * `.save()` changes the status — creating a delivery writes none, and an
 * `.update()` writes none — so the history can lag behind. The delivery itself
 * is always current.
 *
 * Works on both shapes the tracking endpoints answer with: the detail and a
 * search result both carry `delivery.status` and `delivery.substatus`.
 *
 * @param {object} order
 * @returns {{status: string, substatus: string}|null} `null` without a shipment.
 */
export const currentStatusOf = (order) => {
  const delivery = order?.delivery;
  if (!delivery?.status) return null;

  return { status: delivery.status, substatus: delivery.substatus };
};

/**
 * The shipment's history as a timeline, oldest first, with delivery statuses
 * only — none of the order's own.
 *
 * @param {object} order
 */
export const shipmentTimelineOf = (order) =>
  toTimelineEvents(order?.delivery?.status_history, DELIVERY_STATUS_LABELS);

/**
 * The WhatsApp conversation for this order, opened against whoever can answer
 * about it: the store's own line when it has one, and Ninja Hubs otherwise.
 *
 * @param {object} order - As `customer_track_order` returns it.
 * @param {string|number} orderId
 * @returns {string}
 */
export const buildSupportWhatsappLink = (order, orderId) => {
  const phone = order?.warehouse?.customer_phone || SUPPORT_PHONE;
  const company = order?.seller?.company?.social_reason;
  const message = company
    ? `Hola, necesito ayuda con mi pedido #${orderId} de ${company}`
    : `Hola, necesito ayuda con mi pedido #${orderId}`;

  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
};
